import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeToggle from '../components/ThemeToggle';
import api from '../api/client';
import { fetchActiveLessonSession } from '../api/activeLesson';
import {
  getStudentHomePrimaryItems,
  getStudentMoreItems,
} from '../studentHub';

function HubTile({ item }) {
  const className = [
    'quick-link-card',
    item.live ? 'quick-link-card--live' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      <span className="quick-link-icon" aria-hidden>
        <i className={`fa-solid ${item.icon}`} />
      </span>
      <span>{item.label}</span>
    </>
  );

  if (item.external) {
    return (
      <a
        href={item.external}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        title={item.title || item.label}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={item.to} className={className} title={item.title || item.label}>
      {content}
    </Link>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeLesson, setActiveLesson] = useState(null);
  const [topicPrograms, setTopicPrograms] = useState([]);

  useEffect(() => {
    fetchSolutions();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchActiveLessonSession()
      .then((active) => {
        if (!cancelled) setActiveLesson(active);
      })
      .catch(() => {
        if (!cancelled) setActiveLesson(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks/topic-programs/menu/');
        const data = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        if (!cancelled) setTopicPrograms(data);
      } catch {
        if (!cancelled) setTopicPrograms([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function fetchSolutions() {
    try {
      const res = await api.get('/tasks/my-solutions/');
      setSolutions(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setError('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }

  const primaryTiles = useMemo(
    () => getStudentHomePrimaryItems({ user, activeLesson }),
    [user, activeLesson],
  );
  const moreTiles = useMemo(
    () => getStudentMoreItems({ user, topicPrograms }),
    [user, topicPrograms],
  );

  const totalSolved = solutions.filter((s) => s.status === 'accepted').length;
  const totalSubmissions = solutions.length;
  const acceptanceRate = totalSubmissions > 0
    ? Math.round((totalSolved / totalSubmissions) * 100)
    : 0;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header dashboard-header-with-toggle">
        <div>
          <h1>Добро пожаловать, {user?.first_name || user?.username}!</h1>
          <p className="page-subtitle">Ваш прогресс обучения</p>
        </div>
        <ThemeToggle />
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="dashboard-section hub-section">
        <h2>Основные разделы</h2>
        <div className="quick-links">
          {primaryTiles.map((item) => (
            <HubTile key={item.id} item={item} />
          ))}
        </div>
      </section>

      {moreTiles.length > 0 && (
        <section className="dashboard-section hub-section">
          <h2>Ещё</h2>
          <div className="quick-links">
            {moreTiles.map((item) => (
              <HubTile key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{totalSolved}</div>
          <div className="stat-label">Задач решено</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalSubmissions}</div>
          <div className="stat-label">Всего попыток</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{acceptanceRate}%</div>
          <div className="stat-label">Процент успеха</div>
        </div>
      </div>

      <div className="dashboard-sections">
        <section className="dashboard-section">
          <h2>Последние решения</h2>
          {solutions.length === 0 ? (
            <div className="empty-state">
              <p>У вас пока нет решений</p>
              <Link to="/catalog" className="btn btn-primary">Открыть каталог задач</Link>
            </div>
          ) : (
            <div className="solutions-list">
              {solutions.slice(0, 10).map((sol, i) => (
                <div key={i} className="solution-item">
                  <div className="solution-info">
                    <span className="solution-task">
                      {sol.task_title || sol.task?.title || `Задача #${sol.task_id || sol.task}`}
                    </span>
                    <span className="solution-date">
                      {sol.created_at ? new Date(sol.created_at).toLocaleDateString('ru-RU') : ''}
                    </span>
                  </div>
                  <span className={`solution-status status-${sol.status}`}>
                    {sol.status === 'accepted' ? '✅ Принято' :
                     sol.status === 'wrong_answer' ? '❌ Неверно' :
                     sol.status === 'error' ? '⚠️ Ошибка' :
                     sol.status || 'На проверке'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
